/**
 * One language's progress (P5 Language Learning), from canonical results.
 * Pure presentation: no store, no network. An unmeasured value is shown as
 * "Not measured", never 0; pronunciation and fluency are unavailable.
 */
import type { LanguageProgress } from '../../store/api/languageLearningApi';
import { displayChange, displayMeasured } from '../../utils/languageLearning';

/** One language's progress. Unmeasured is "Not measured", never 0. */
export function LanguageProgressCard({ progress }: { progress: LanguageProgress }) {
  return (
    <section className="rounded-lg border border-gray-200 p-4 space-y-3" data-testid={`language-progress-${progress.language}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-lg font-semibold text-gray-900">{progress.label}</h2>
        {progress.latestSelfReportedLevel && (
          <span className="text-xs text-gray-600">Your level: {progress.latestSelfReportedLevel.level} (self-reported)</span>
        )}
      </div>
      <p className="text-sm text-gray-700">
        {progress.attempts.total} completed conversation{progress.attempts.total === 1 ? '' : 's'}
        {' '}({progress.attempts.development} practice, {progress.attempts.testing} graded).
        {' '}Latest overall: {displayMeasured(progress.overall.latest)}
        {progress.overall.unknown > 0 && ` · ${progress.overall.unknown} not measured`}
      </p>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-gray-500">
            <th className="py-1">Skill</th><th>First</th><th>Latest</th><th>Change</th><th>Not measured</th>
          </tr>
        </thead>
        <tbody>
          {progress.dimensions.map((d) => (
            <tr key={d.key} className="border-t border-gray-100" data-testid={`dimension-${d.key}`}>
              <td className="py-1">{d.label}</td>
              <td>{displayMeasured(d.first)}</td>
              <td>{displayMeasured(d.latest)}</td>
              <td>{displayChange(d.change) ?? '—'}</td>
              <td>{d.unknown}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <ul className="text-xs text-gray-500">
        {progress.unavailable.map((u) => (
          <li key={u.key} data-testid={`unavailable-${u.key}`}>{u.label}: unavailable — {u.reason}</li>
        ))}
      </ul>
    </section>
  );
}
